import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, X, Database, Loader2, ChevronLeft, Mail } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { useAppData } from '../../contexts/AppDataContext';
import { getTableTypeIcon } from '../../utils/tableUtils';
import { formatRelativeTime } from '../../utils/dateUtils';

interface Dataset {
  id: string;
  name: string;
  updated_at: string;
  table_type?: string;
  lead_count?: number;
}

interface Column {
  key: string;
  name: string;
  column_type?: string;
}

interface DatasetGroups {
  today: Dataset[];
  yesterday: Dataset[];
  thisWeek: Dataset[];
  older: Dataset[];
}

interface CampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  datasets: Dataset[];
  onNewCampaign: () => void;
}

const groupDatasetsByTime = (datasets: Dataset[]): DatasetGroups => {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const groups: DatasetGroups = { today: [], yesterday: [], thisWeek: [], older: [] };

  datasets.forEach(dataset => {
    const date = new Date(dataset.updated_at);
    if (date >= todayStart) groups.today.push(dataset);
    else if (date >= yesterdayStart) groups.yesterday.push(dataset);
    else if (date >= sevenDaysAgo) groups.thisWeek.push(dataset);
    else groups.older.push(dataset);
  });

  return groups;
};

export const CampaignModal: React.FC<CampaignModalProps> = ({
  isOpen,
  onClose,
  datasets,
  onNewCampaign,
}) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { refreshCampaigns } = useAppData();
  const inputRef = useRef<HTMLInputElement>(null);

  // Step state
  const [step, setStep] = useState<'select-dataset' | 'select-email-column'>('select-dataset');
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [columns, setColumns] = useState<Column[]>([]);
  const [emailColumn, setEmailColumn] = useState<string>('');

  // Loading states
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Dataset picker state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const filteredDatasets = useMemo(() =>
    (datasets || []).filter(d => d.name?.toLowerCase().includes(searchQuery.toLowerCase())),
    [datasets, searchQuery]
  );

  const groupedDatasets = useMemo(() => groupDatasetsByTime(filteredDatasets), [filteredDatasets]);

  const allItems = useMemo(() => [
    { type: 'action' as const },
    ...groupedDatasets.today.map(d => ({ type: 'dataset' as const, data: d })),
    ...groupedDatasets.yesterday.map(d => ({ type: 'dataset' as const, data: d })),
    ...groupedDatasets.thisWeek.map(d => ({ type: 'dataset' as const, data: d })),
    ...groupedDatasets.older.map(d => ({ type: 'dataset' as const, data: d })),
  ], [groupedDatasets]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('select-dataset');
      setSelectedDataset(null);
      setColumns([]);
      setEmailColumn('');
      setSearchQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Reset selected index when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  const handleClose = useCallback(() => {
    if (isCreating || isLoadingColumns) return;
    onClose();
  }, [onClose, isCreating, isLoadingColumns]);

  const handleDatasetSelect = useCallback(async (dataset: Dataset) => {
    if (isLoadingColumns || isCreating) return;

    setSelectedDataset(dataset);
    setIsLoadingColumns(true);

    try {
      const response = await apiClient.getTableColumns(dataset.id);
      const cols = response.columns || [];
      setColumns(cols);

      // Auto-select email column if found
      const emailCol = cols.find(
        (col) =>
          col.column_type === 'email' ||
          col.key.toLowerCase().includes('email') ||
          col.name.toLowerCase().includes('email')
      );
      setEmailColumn(emailCol?.name || '');
      setStep('select-email-column');
    } catch (error) {
      console.error('Failed to load columns:', error);
      showToast('Failed to load dataset columns', 'error');
      setSelectedDataset(null);
    } finally {
      setIsLoadingColumns(false);
    }
  }, [showToast, isLoadingColumns, isCreating]);

  const handleCreateCampaign = useCallback(async () => {
    if (!selectedDataset || !emailColumn || isCreating) return;

    setIsCreating(true);

    try {
      const campaign = await apiClient.createCampaign({
        name: `Campaign - ${selectedDataset.name}`,
        description: `Campaign targeting ${selectedDataset.name}`,
        dataset_ids: [selectedDataset.id],
      });

      try {
        await apiClient.enrollFromDataset(campaign.id, selectedDataset.id, {
          emailColumn: emailColumn,
        });
      } catch (error) {
        console.error('Failed to enroll recipients:', error);
      }

      showToast('Campaign created successfully', 'success');
      refreshCampaigns();
      onClose();
      navigate(`/campaigns/${campaign.id}`);
    } catch (error) {
      console.error('Failed to create campaign:', error);
      showToast('Failed to create campaign', 'error');
    } finally {
      setIsCreating(false);
    }
  }, [selectedDataset, emailColumn, navigate, onClose, showToast, refreshCampaigns, isCreating]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen || step !== 'select-dataset') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isLoadingColumns) return;

      switch (e.key) {
        case 'Escape':
          handleClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, allItems.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter': {
          e.preventDefault();
          const selected = allItems[selectedIndex];
          if (selected?.type === 'action') {
            onNewCampaign();
            onClose();
          } else if (selected?.type === 'dataset' && selected.data) {
            handleDatasetSelect(selected.data);
          }
          break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, step, selectedIndex, allItems, handleClose, onNewCampaign, onClose, handleDatasetSelect, isLoadingColumns]);

  const getItemIndex = useCallback((groupName: string, indexInGroup: number): number => {
    let index = 1;
    if (groupName === 'today') return index + indexInGroup;
    index += groupedDatasets.today.length;
    if (groupName === 'yesterday') return index + indexInGroup;
    index += groupedDatasets.yesterday.length;
    if (groupName === 'thisWeek') return index + indexInGroup;
    index += groupedDatasets.thisWeek.length;
    return index + indexInGroup;
  }, [groupedDatasets]);

  const renderDatasetGroup = useCallback((groupTitle: string, items: Dataset[], groupKey: string, isFirst: boolean = false) => {
    if (items.length === 0) return null;

    return (
      <div className="mb-1" key={groupKey}>
        {!isFirst ? (
          <div className="flex items-center gap-3 px-5 pt-4 pb-2">
            <div className="h-px flex-1 bg-neutral-100" />
            <span className="text-[11px] text-neutral-400 font-medium tracking-wide">{groupTitle}</span>
            <div className="h-px flex-1 bg-neutral-100" />
          </div>
        ) : (
          <div className="px-5 pt-1 pb-2">
            <span className="text-[11px] text-neutral-400 font-medium tracking-wide">{groupTitle}</span>
          </div>
        )}
        <div className="space-y-0.5">
          {items.map((dataset, idx) => {
            const itemIndex = getItemIndex(groupKey, idx);
            const isSelected = selectedIndex === itemIndex;
            const Icon = getTableTypeIcon(dataset.table_type);
            const isThisLoading = isLoadingColumns && selectedDataset?.id === dataset.id;
            const isDisabled = isLoadingColumns && !isThisLoading;

            return (
              <button
                key={dataset.id}
                onClick={() => handleDatasetSelect(dataset)}
                onMouseEnter={() => !isLoadingColumns && setSelectedIndex(itemIndex)}
                disabled={isLoadingColumns}
                className={`w-full flex items-center gap-4 px-5 py-3 text-left transition-all duration-150 ${
                  isThisLoading
                    ? 'bg-neutral-50'
                    : isDisabled
                      ? 'opacity-50 cursor-not-allowed'
                      : isSelected ? 'bg-neutral-50' : 'hover:bg-neutral-50/50'
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                  isSelected || isThisLoading ? 'bg-neutral-200' : 'bg-neutral-100'
                }`}>
                  {isThisLoading ? (
                    <Loader2 className="w-4 h-4 text-neutral-500 animate-spin" />
                  ) : (
                    <Icon className="w-4 h-4 text-neutral-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="block text-[14px] text-neutral-800 truncate font-medium leading-tight">
                    {isThisLoading ? 'Loading columns...' : (dataset.name || 'Untitled Dataset')}
                  </span>
                  <div className="flex items-center gap-2 text-[12px] text-neutral-400">
                    {isThisLoading ? (
                      <span>Please wait</span>
                    ) : (
                      <>
                        <span>{formatRelativeTime(dataset.updated_at)}</span>
                        {dataset.lead_count != null && (
                          <>
                            <span>·</span>
                            <span>{dataset.lead_count.toLocaleString()} results</span>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }, [getItemIndex, selectedIndex, isLoadingColumns, selectedDataset, handleDatasetSelect]);

  if (!isOpen) return null;

  // Email column selection step
  if (step === 'select-email-column' && selectedDataset) {
    const emailColumns = columns.filter(
      (col) =>
        col.column_type === 'email' ||
        col.key.toLowerCase().includes('email') ||
        col.name.toLowerCase().includes('email')
    );
    const otherColumns = columns.filter(
      (col) =>
        col.column_type !== 'email' &&
        !col.key.toLowerCase().includes('email') &&
        !col.name.toLowerCase().includes('email')
    );

    return (
      <>
        <div className="fixed inset-0 bg-black/40 z-[200]" onClick={handleClose} />
        <div className="fixed inset-0 z-[201] flex items-start justify-center pt-[10vh]" onClick={handleClose}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-[480px] overflow-hidden border border-neutral-100 animate-fadeInScale"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 pt-5 pb-4 border-b border-neutral-100">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setStep('select-dataset')}
                  disabled={isCreating}
                  className="p-1.5 -ml-1.5 rounded-lg hover:bg-neutral-100 transition-colors disabled:opacity-50"
                >
                  <ChevronLeft className="w-5 h-5 text-neutral-500" />
                </button>
                <div>
                  <h2 className="text-[16px] font-semibold text-neutral-900">Confirm Email Column</h2>
                  <p className="text-[13px] text-neutral-500">Select which column contains email addresses</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-5">
              <div className="mb-4 p-3 bg-neutral-50 rounded-xl">
                <p className="text-[11px] text-neutral-400 uppercase tracking-wide font-medium">Dataset</p>
                <p className="text-[14px] font-medium text-neutral-900 truncate mt-0.5">
                  {selectedDataset.name}
                </p>
              </div>

              <label className="block text-[12px] font-medium text-neutral-600 mb-2">
                Email Column
              </label>
              <select
                value={emailColumn}
                onChange={(e) => setEmailColumn(e.target.value)}
                disabled={isCreating}
                className="w-full px-3 py-2.5 text-[14px] border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-neutral-200 focus:border-neutral-300 transition-all disabled:opacity-50"
              >
                <option value="">Select a column...</option>
                {emailColumns.length > 0 && (
                  <optgroup label="Email Columns">
                    {emailColumns.map((col) => (
                      <option key={col.key} value={col.name}>
                        {col.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                {otherColumns.length > 0 && (
                  <optgroup label="Other Columns">
                    {otherColumns.map((col) => (
                      <option key={col.key} value={col.name}>
                        {col.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>

              {!emailColumn && (
                <p className="mt-2 text-[12px] text-amber-600">
                  Please select the column that contains recipient email addresses
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-neutral-100 flex justify-end gap-3 bg-neutral-50/50">
              <button
                onClick={handleClose}
                disabled={isCreating}
                className="px-4 py-2 text-[13px] font-medium text-neutral-600 hover:text-neutral-900 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCampaign}
                disabled={!emailColumn || isCreating}
                className="flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium text-white bg-neutral-900 rounded-xl hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Create Campaign
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Dataset selection step
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[200]" onClick={handleClose} />
      <div className="fixed inset-0 z-[201] flex items-start justify-center pt-[10vh]" onClick={handleClose}>
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-[640px] overflow-hidden border border-neutral-100 animate-fadeInScale"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-5 pt-5 pb-4 border-b border-neutral-100">
            <h2 className="text-[16px] font-semibold text-neutral-900 mb-1">Start a Campaign</h2>
            <p className="text-[13px] text-neutral-500">Choose a dataset below to create a new campaign with those recipients.</p>
          </div>

          <div className="px-5 py-3 bg-neutral-50/50">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search datasets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={isLoadingColumns}
                className="w-full pl-12 pr-10 py-2.5 text-[15px] bg-white border border-neutral-200 rounded-xl outline-none placeholder:text-neutral-400 focus:border-neutral-300 focus:ring-2 focus:ring-neutral-100 transition-all disabled:opacity-50"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-neutral-400 hover:text-neutral-600 rounded-full hover:bg-neutral-100"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[440px] overflow-y-auto">
            <div className="px-4 pt-3 pb-2">
              <button
                onClick={() => {
                  if (isLoadingColumns) return;
                  onNewCampaign();
                  onClose();
                }}
                onMouseEnter={() => !isLoadingColumns && setSelectedIndex(0)}
                disabled={isLoadingColumns}
                className={`w-full flex items-center gap-4 px-4 py-3.5 text-left transition-all duration-150 rounded-xl ${
                  isLoadingColumns
                    ? 'opacity-50 cursor-not-allowed bg-neutral-100 text-neutral-700'
                    : selectedIndex === 0 ? 'bg-neutral-900 text-white' : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                  selectedIndex === 0 && !isLoadingColumns ? 'bg-white/20' : 'bg-white'
                }`}>
                  <Plus className={`w-5 h-5 ${selectedIndex === 0 && !isLoadingColumns ? 'text-white' : 'text-neutral-600'}`} />
                </div>
                <div>
                  <span className="block text-[14px] font-semibold">New Dataset</span>
                  <span className={`text-[12px] ${selectedIndex === 0 && !isLoadingColumns ? 'text-neutral-300' : 'text-neutral-500'}`}>
                    Create a dataset first, then start your campaign
                  </span>
                </div>
              </button>
            </div>

            <div className="pb-3">
              {filteredDatasets.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-neutral-100 flex items-center justify-center mb-4">
                    <Database className="w-7 h-7 text-neutral-300" />
                  </div>
                  <p className="text-[14px] text-neutral-500 font-medium">
                    {searchQuery ? 'No matching datasets' : 'No datasets yet'}
                  </p>
                  <p className="text-[13px] text-neutral-400 mt-1">
                    {searchQuery ? 'Try a different search term' : 'Create a dataset first'}
                  </p>
                </div>
              ) : (
                <>
                  {renderDatasetGroup('Today', groupedDatasets.today, 'today', true)}
                  {renderDatasetGroup('Yesterday', groupedDatasets.yesterday, 'yesterday', groupedDatasets.today.length === 0)}
                  {renderDatasetGroup('This Week', groupedDatasets.thisWeek, 'thisWeek', groupedDatasets.today.length === 0 && groupedDatasets.yesterday.length === 0)}
                  {renderDatasetGroup('Older', groupedDatasets.older, 'older', groupedDatasets.today.length === 0 && groupedDatasets.yesterday.length === 0 && groupedDatasets.thisWeek.length === 0)}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
